import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/react'
import './lib/maplibreWorker'
import { installDomTranslateGuard } from './lib/domTranslateGuard'
import { App } from './App'

// Make React resilient to Google Translate / in-page translation rewriting the
// DOM (otherwise the whole app crashes into the error boundary). Must run
// before the first render.
installDomTranslateGuard()
import { initPostHog, registerSuperProps } from './lib/posthog'
import { initSentry } from './lib/sentry'
import { initErrorTracking } from './lib/errorTracking'
import { captureLaunchCohort } from './services/retention/cohort'
import './index.css'
import { i18nReady } from './i18n'

// When Vite can't load a lazy chunk (old hash evicted by Vercel after a new
// deploy), reload once to get fresh index.html. sessionStorage guard prevents
// an infinite loop if the new bundle is itself broken.
window.addEventListener('vite:preloadError', () => {
  if (!sessionStorage.getItem('bs_chunk_reload')) {
    sessionStorage.setItem('bs_chunk_reload', '1')
    window.location.reload()
  }
})

// First-touch launch-cohort capture (UTM source + which tool door) BEFORE
// analytics init, so initPostHog can attach it as super properties to every
// event. First-touch: a stored cohort is never overwritten by later navigation.
captureLaunchCohort(window.location.search)

// Initialize analytics & error tracking before rendering
initPostHog()
initSentry()
initErrorTracking()

const root = document.getElementById('root')
if (!root) throw new Error('Root element not found')

// Wait for the active language bundle to load before the first render so
// `t()` resolves real copy instead of flashing raw keys (useSuspense is off).
// `finally` ensures we still render if a locale chunk fails to load.
void i18nReady.finally(() => {
  createRoot(root).render(
    <StrictMode>
      <App />
      <Analytics />
      <SpeedInsights />
    </StrictMode>,
  )
})

// Non-critical, fire-and-forget side effects. Both are only consumed by
// logged-in features (the Wingman RAG store, the scan inference client)
// that are themselves lazy-loaded deep in the app, so they must not sit
// in the synchronous entry chunk or run before first paint. Dynamic-
// importing them on idle keeps ragService (~900 lines) + its graph and
// the inference/scan graph out of the critical path, improving FCP/LCP
// with no behavioural change (nothing awaits these today either).
const runDeferredInit = () => {
  // Real User Monitoring: forward Core Web Vitals (LCP/CLS/INP/TTFB/FCP) to
  // PostHog as `web_vitals` events tagged with the deploy id, so field
  // performance can be trended per release alongside product events and launch
  // cohorts (Vercel Speed Insights collects the same signals but in a siloed
  // dashboard that can't be joined to funnels). Consent-gated + no-op when
  // PostHog is unconfigured, so this is safe in dev/staging. Deferred to idle —
  // the web-vitals library uses buffered observers, so metrics that fired
  // before this runs are still captured.
  void import('./lib/webVitals').then(({ initWebVitals }) => initWebVitals())
  // If VITE_BEETLESENSE_INFERENCE_URL + KEY are set, swap the mock
  // inference client for the cloud one; otherwise the mock keeps running.
  void import('./services/cloudInferenceClient').then((m) => m.maybeRegisterCloudClient())
  // Adds the curated knowledge-base JSON (95+ entries with DOIs, topic
  // tags, abstracts) to the RAG store so the Wingman retrieves over the
  // same union of sources visible on /research. Failures are non-fatal:
  // the hardcoded RESEARCH_SOURCES still serve as the floor.
  void import('./services/ragKnowledgeBaseBootstrap').then((m) => m.bootstrapRagKnowledgeBase())
  // Offline-first sync bootstrap: registers the survey-order sync handler,
  // enables auto-sync on reconnect (idempotent — also enabled by the
  // OfflineIndicator hook), and drains any orders queued in a previous
  // offline session (UI-003, critical-journey edge case 2).
  void import('./services/surveyOrderQueue').then((m) => m.initSurveyOrderSync())
  // Same canonical engine: register the field-observation table handler so
  // observations queued offline replay on reconnect (mirrors the legacy
  // offlineSyncManager's field_observations semantics, incl. RLS 42501
  // permanent-dequeue). Producer migration off lib/offlineStore is tracked in
  // TECH_DEBT.
  void import('./lib/fieldObservationSync').then((m) => m.initFieldObservationSync())
  // Tool-launch attribution fallback: a visitor who lands directly on a tool
  // page (search result, shared link, magazine cross-link) carries no
  // `?launchTool=` param, so the eager captureLaunchCohort() call above never
  // sees one — "which tool drives signups" was going unattributed for the
  // large majority of first touches. Deferred + dynamically imported (not a
  // top-level import) so the full blade registry never enters the critical
  // entry chunk; first-touch semantics mean this only ever writes when
  // nothing was captured already, so it can't clobber a real UTM cohort.
  void import('./services/blades/bladeRegistry').then(({ getBladeByRoute }) => {
    const blade = getBladeByRoute(window.location.pathname)
    if (!blade) return
    const cohort = captureLaunchCohort(window.location.search, blade.id)
    if (cohort.launchTool === blade.id) {
      registerSuperProps({ launch_tool: blade.id })
    }
  })
}
if ('requestIdleCallback' in window) {
  window.requestIdleCallback(runDeferredInit, { timeout: 4000 })
} else {
  setTimeout(runDeferredInit, 1500)
}
